import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, Grid, Paper, Card, CardContent, CardMedia,
  Chip, Button, CircularProgress, Alert, Accordion, AccordionSummary,
  AccordionDetails, Breadcrumbs, Link as MuiLink, useMediaQuery, useTheme,
  Tooltip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Schedule as TimeIcon,
  AttachMoney as BudgetIcon,
  Language as LanguageIcon,
  Security as SafetyIcon,
  WbSunny as SunIcon,
  Cloud as CloudIcon,
  Thunderstorm as RainIcon,
  NavigateNext as NavNextIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandIcon,
  Star as StarIcon,
  FlightTakeoff as FlightIcon,
  Lightbulb as TipIcon,
  QuestionAnswer as FaqIcon,
  NearMe as NearbyIcon,
  Museum as MuseumIcon,
  Restaurant as FoodIcon,
  Park as ParkIcon,
  BeachAccess as BeachIcon,
  ShoppingBag as ShoppingIcon,
  Nightlife as NightlifeIcon,
  Hiking as HikingIcon,
  Landscape as LandscapeIcon,
  Favorite as FavoriteIcon,
  MenuBook as GuideIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UnsplashAttribution from '../components/UnsplashAttribution';
import SEOHead from '../components/SEOHead';
import api from '../config/api';
import { THEME_COLORS } from '../constants/theme';

const categoryIcons = {
  landmark: <MuseumIcon />,
  museum: <MuseumIcon />,
  food: <FoodIcon />,
  park: <ParkIcon />,
  nature: <LandscapeIcon />,
  beach: <BeachIcon />,
  shopping: <ShoppingIcon />,
  nightlife: <NightlifeIcon />,
  neighborhood: <LocationIcon />,
  entertainment: <NightlifeIcon />,
  experience: <HikingIcon />,
  other: <LocationIcon />,
};

const categoryColors = {
  landmark: '#b45309',
  museum: '#7c3aed',
  food: '#059669',
  park: '#16a34a',
  nature: '#0d9488',
  beach: '#0284c7',
  shopping: '#db2777',
  nightlife: '#7c3aed',
  neighborhood: '#6366f1',
  entertainment: '#ea580c',
  experience: '#0891b2',
  other: '#6b7280',
};

const tipCategoryIcons = {
  transport: '🚇',
  food: '🍽️',
  culture: '🎭',
  money: '💰',
  safety: '🛡️',
  packing: '🎒',
};

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DestinationDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDestination = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/destinations/${slug}`);
        setDestination(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load destination');
      } finally {
        setLoading(false);
      }
    };
    fetchDestination();
  }, [slug]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !destination) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Destination not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/destinations')} sx={{ mt: 2 }}>
          {t('common.back', 'Back')}
        </Button>
      </Container>
    );
  }

  const tags = destination.popular_for ? (typeof destination.popular_for === 'string' ? JSON.parse(destination.popular_for) : destination.popular_for) : [];
  const maxTemp = destination.weather?.length > 0 ? Math.max(...destination.weather.map(w => w.avg_high_c)) : 30;
  const minTemp = destination.weather?.length > 0 ? Math.min(...destination.weather.map(w => w.avg_low_c)) : 0;
  const tempRange = maxTemp - minTemp || 1;

  // Structured data for SEO
  const destinationSchema = {
    "@context": "https://schema.org",
    "@type": "TouristDestination",
    "name": destination.name,
    "description": destination.description,
    "url": `https://dejavunomad.com/destinations/${slug}`,
    ...(destination.image_url && { "image": destination.image_url }),
    "touristType": tags,
    "geo": destination.latitude && destination.longitude ? {
      "@type": "GeoCoordinates",
      "latitude": destination.latitude,
      "longitude": destination.longitude
    } : undefined,
    "containedInPlace": {
      "@type": "Country",
      "name": destination.country
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dejavunomad.com" },
      { "@type": "ListItem", "position": 2, "name": "Destinations", "item": "https://dejavunomad.com/destinations" },
      { "@type": "ListItem", "position": 3, "name": `${destination.name}, ${destination.country}`, "item": `https://dejavunomad.com/destinations/${slug}` }
    ]
  };

  const faqSchema = destination.faqs?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": destination.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const structuredData = [destinationSchema, breadcrumbSchema, faqSchema].filter(Boolean);

  return (
    <>
      <SEOHead
        title={`${destination.name}, ${destination.country} — ${t('destinations.thingsToDo', 'Things to Do')}, ${t('destinations.travelTips', 'Tips')} & ${t('destinations.weather', 'Weather')} | Déjà Vu Nomad`}
        description={destination.description || `Discover the best things to do in ${destination.name}, ${destination.country}. Weather, travel tips, attractions, and travel guides.`}
        canonical={`https://dejavunomad.com/destinations/${slug}`}
        ogImage={destination.image_url || '/noBgColor.png'}
        keywords={`${destination.name}, ${destination.country}, ${destination.continent || ''}, things to do in ${destination.name}, ${destination.name} travel guide, ${destination.name} weather, ${tags.join(', ')}`}
        structuredData={structuredData}
      />

      {/* Hero Section with Image */}
      <Box sx={{
        position: 'relative',
        background: destination.image_url
          ? `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7)), url(${destination.image_url}) center/cover`
          : `linear-gradient(135deg, ${THEME_COLORS.primary.main} 0%, ${THEME_COLORS.secondary.main} 100%)`,
        color: 'white',
        py: { xs: 4, md: 6 },
        minHeight: { xs: 250, md: 350 },
        display: 'flex', alignItems: 'flex-end',
      }}>
        <UnsplashAttribution
          imageUrl={destination.image_url}
          photographerName={destination.unsplash_photographer_name}
          photographerUsername={destination.unsplash_photographer_username}
          variant="overlay"
        />
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ position: 'relative', zIndex: 1 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs separator={<NavNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />} sx={{ mb: 2 }}>
            <MuiLink component={Link} to="/destinations" underline="hover" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
              {t('destinations.pageTitle', 'Destinations')}
            </MuiLink>
            <MuiLink component={Link} to={`/destinations?continent=${destination.continent}`} underline="hover" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
              {destination.continent}
            </MuiLink>
            <Typography variant="body2" sx={{ color: 'white' }}>{destination.name}</Typography>
          </Breadcrumbs>

          {/* Tags */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            {tags.slice(0, 5).map(tag => (
              <Chip key={tag} label={tag} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, backdropFilter: 'blur(4px)' }} />
            ))}
          </Box>

          <Typography variant={isMobile ? 'h4' : 'h2'} fontWeight={800} sx={{ textShadow: '0 2px 12px rgba(0,0,0,0.3)', mb: 0.5 }}>
            {destination.name}
          </Typography>
          <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationIcon sx={{ fontSize: 20 }} />
            {destination.country} · {destination.continent}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, mt: 1, maxWidth: 600, display: { xs: 'none', md: 'block' } }}>
            {destination.description}
          </Typography>

          {/* CTA */}
          <Button
            variant="contained"
            startIcon={<FlightIcon />}
            onClick={() => navigate('/trips/create')}
            sx={{
              mt: 2, bgcolor: 'white', color: THEME_COLORS.primary.main, fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              borderRadius: 2, px: 3,
            }}
          >
            {t('destinations.planTrip', 'Plan a Trip Here')}
          </Button>
        </Container>
      </Box>

      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: { xs: 3, md: 4 } }}>
        {/* Mobile description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, display: { xs: 'block', md: 'none' } }}>
          {destination.description}
        </Typography>

        {/* Quick Facts Grid */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { icon: <BudgetIcon />, label: t('destinations.dailyBudget', 'Daily Budget'), value: destination.avg_daily_budget },
            { icon: <LanguageIcon />, label: t('destinations.language', 'Language'), value: destination.language },
            { icon: <Typography sx={{ fontSize: 20 }}>{destination.currency_symbol}</Typography>, label: t('destinations.currency', 'Currency'), value: destination.currency },
            { icon: <SunIcon />, label: t('destinations.bestTime', 'Best Time to Visit'), value: destination.best_time_to_visit },
            { icon: <SafetyIcon />, label: t('destinations.safety', 'Safety'), value: <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3 }}>{Array.from({ length: 5 }, (_, i) => <StarIcon key={i} sx={{ fontSize: 16, color: i < destination.safety_rating ? '#f59e0b' : '#e5e7eb' }} />)}</Box> },
            { icon: <TimeIcon />, label: t('destinations.timezone', 'Timezone'), value: destination.timezone },
          ].filter(f => f.value).map((fact, i) => (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                height: '100%', textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: THEME_COLORS.primary.main, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
              }}>
                <Box sx={{ color: THEME_COLORS.primary.main, mb: 0.5 }}>{fact.icon}</Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {fact.label}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 0.3, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                  {fact.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Weather Section */}
        {destination.weather?.length > 0 && (
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 4 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SunIcon sx={{ color: '#f59e0b' }} />
              {t('destinations.weatherTitle', 'Weather Throughout the Year')}
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(6, 1fr)', sm: 'repeat(12, 1fr)' },
              gap: 1, mt: 2,
            }}>
              {destination.weather.map((w, i) => {
                const highPercent = ((w.avg_high_c - minTemp) / tempRange) * 100;
                const lowPercent = ((w.avg_low_c - minTemp) / tempRange) * 100;
                const isHottest = w.avg_high_c === maxTemp;
                const barColor = w.avg_high_c >= 30 ? '#ef4444' : w.avg_high_c >= 20 ? '#f59e0b' : w.avg_high_c >= 10 ? '#3b82f6' : '#94a3b8';
                return (
                  <Tooltip
                    key={i}
                    title={`${monthNames[i]}: ${w.avg_high_c}°/${w.avg_low_c}°C, ${w.avg_rainfall_mm}mm rain, ${w.avg_rainy_days} rainy days`}
                    arrow
                  >
                    <Box sx={{ textAlign: 'center', cursor: 'pointer' }}>
                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem', color: isHottest ? '#ef4444' : 'text.secondary' }}>
                        {monthNames[i]}
                      </Typography>
                      {/* Temperature bar */}
                      <Box sx={{ height: 80, position: 'relative', mx: 'auto', width: '100%', mt: 0.5 }}>
                        <Box sx={{
                          position: 'absolute',
                          bottom: `${lowPercent}%`,
                          left: '25%', right: '25%',
                          height: `${highPercent - lowPercent}%`,
                          minHeight: 8,
                          bgcolor: barColor,
                          borderRadius: 1,
                          opacity: 0.7,
                          transition: 'opacity 0.2s',
                          '&:hover': { opacity: 1 },
                        }} />
                      </Box>
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.65rem', color: barColor }}>
                        {Math.round(w.avg_high_c)}°
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary', display: 'block' }}>
                        {Math.round(w.avg_low_c)}°
                      </Typography>
                      {/* Rainfall indicator */}
                      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                        {w.avg_rainfall_mm > 150 ? <RainIcon sx={{ fontSize: 12, color: '#3b82f6' }} /> :
                         w.avg_rainfall_mm > 50 ? <CloudIcon sx={{ fontSize: 12, color: '#94a3b8' }} /> :
                         <SunIcon sx={{ fontSize: 12, color: '#f59e0b' }} />}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 3, mt: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SunIcon sx={{ fontSize: 14, color: '#f59e0b' }} /> {t('destinations.dry', 'Dry')} (&lt;50mm)
              </Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CloudIcon sx={{ fontSize: 14, color: '#94a3b8' }} /> {t('destinations.moderate', 'Moderate')} (50-150mm)
              </Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RainIcon sx={{ fontSize: 14, color: '#3b82f6' }} /> {t('destinations.wet', 'Wet')} (&gt;150mm)
              </Typography>
            </Box>
          </Paper>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Attractions & Tips */}
          <Grid item xs={12} md={8}>
            {/* Attractions */}
            {destination.attractions?.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MuseumIcon sx={{ color: THEME_COLORS.primary.main }} />
                  {t('destinations.thingsToDo', 'Things to Do')} in {destination.name}
                </Typography>
                <Grid container spacing={2}>
                  {destination.attractions.map((attr, i) => (
                    <Grid item xs={12} sm={6} key={attr.id || i}>
                      <Card elevation={0} sx={{
                        borderRadius: 3, border: '1px solid', borderColor: 'divider',
                        height: '100%', display: 'flex', flexDirection: 'column',
                        transition: 'all 0.2s ease',
                        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)', borderColor: categoryColors[attr.category] || '#6b7280' },
                      }}>
                        {/* Attraction Image */}
                        {attr.image_url ? (
                          <Box sx={{ position: 'relative' }}>
                            <CardMedia
                              component="img"
                              height={isMobile ? 120 : 150}
                              image={attr.image_url}
                              alt={attr.name}
                              sx={{ objectFit: 'cover' }}
                            />
                            <UnsplashAttribution
                              imageUrl={attr.image_url}
                              photographerName={attr.unsplash_photographer_name}
                              photographerUsername={attr.unsplash_photographer_username}
                              variant="overlay"
                            />
                          </Box>
                        ) : (
                          <Box sx={{
                            height: isMobile ? 80 : 100,
                            background: `linear-gradient(135deg, ${categoryColors[attr.category] || '#6b7280'}15, ${categoryColors[attr.category] || '#6b7280'}05)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderBottom: '1px solid', borderColor: 'divider',
                          }}>
                            <Box sx={{ color: categoryColors[attr.category] || '#6b7280', opacity: 0.5, fontSize: 40 }}>
                              {categoryIcons[attr.category] || categoryIcons.other}
                            </Box>
                          </Box>
                        )}
                        <CardContent sx={{ p: 2, flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                              icon={React.cloneElement(categoryIcons[attr.category] || categoryIcons.other, { sx: { fontSize: '14px !important' } })}
                              label={attr.category}
                              size="small"
                              sx={{
                                bgcolor: `${categoryColors[attr.category] || '#6b7280'}15`,
                                color: categoryColors[attr.category] || '#6b7280',
                                fontWeight: 600, fontSize: '0.65rem', height: 24,
                                '& .MuiChip-icon': { color: categoryColors[attr.category] || '#6b7280' },
                              }}
                            />
                          </Box>
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            {attr.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {attr.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Travel Tips */}
            {destination.tips?.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TipIcon sx={{ color: '#f59e0b' }} />
                  {t('destinations.travelTips', 'Travel Tips')}
                </Typography>
                <Grid container spacing={2}>
                  {destination.tips.map((tip, i) => (
                    <Grid item xs={12} sm={6} key={tip.id || i}>
                      <Paper elevation={0} sx={{
                        p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                        height: '100%',
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: '#f59e0b', bgcolor: '#fffbeb' },
                      }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{tipCategoryIcons[tip.category] || '💡'}</span>
                          {tip.category && tip.category.charAt(0).toUpperCase() + tip.category.slice(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {tip.tip}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* FAQs */}
            {destination.faqs?.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FaqIcon sx={{ color: THEME_COLORS.primary.main }} />
                  {t('destinations.faqs', 'Frequently Asked Questions')}
                </Typography>
                {destination.faqs.map((faq, i) => (
                  <Accordion key={faq.id || i} elevation={0} sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: '12px !important',
                    mb: 1, '&::before': { display: 'none' },
                    '&.Mui-expanded': { borderColor: THEME_COLORS.primary.main },
                  }}>
                    <AccordionSummary expandIcon={<ExpandIcon />}>
                      <Typography variant="subtitle2" fontWeight={600}>{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Grid>

          {/* Right Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Plan Trip CTA */}
            <Paper elevation={0} sx={{
              p: 3, borderRadius: 3, mb: 3,
              background: `linear-gradient(135deg, ${THEME_COLORS.primary.main} 0%, ${THEME_COLORS.secondary.main} 100%)`,
              color: 'white', textAlign: 'center',
            }}>
              <FlightIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {t('destinations.readyToGo', 'Ready to go?')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mb: 2 }}>
                {t('destinations.planTripCta', `Start planning your trip to ${destination.name} with our free trip planner.`)}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/trips/create')}
                sx={{ bgcolor: 'white', color: THEME_COLORS.primary.main, fontWeight: 700, '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
              >
                {t('destinations.planTrip', 'Plan a Trip Here')}
              </Button>
            </Paper>

            {/* Related Travel Guides */}
            {destination.guides?.length > 0 && (
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GuideIcon sx={{ color: THEME_COLORS.primary.main }} />
                  {t('destinations.relatedGuides', 'Travel Guides')} for {destination.name}
                </Typography>
                {destination.guides.map(guide => (
                  <Card
                    key={guide.id}
                    onClick={() => navigate(`/guides/${guide.slug}`)}
                    elevation={0}
                    sx={{
                      display: 'flex', mb: 1.5, cursor: 'pointer', borderRadius: 2,
                      border: '1px solid', borderColor: 'divider',
                      transition: 'all 0.2s ease',
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderColor: THEME_COLORS.primary.main },
                    }}
                  >
                    {guide.cover_image && (
                      <CardMedia
                        component="img"
                        image={guide.cover_image}
                        alt={guide.title}
                        sx={{ width: 80, height: 70, objectFit: 'cover' }}
                      />
                    )}
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, flex: 1 }}>
                      <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3, display: 'block', mb: 0.3 }}>
                        {guide.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <TimeIcon sx={{ fontSize: 12 }} /> {guide.duration_days}d
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <FavoriteIcon sx={{ fontSize: 12, color: '#ef4444' }} /> {guide.likes_count || 0}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                <Button
                  fullWidth
                  size="small"
                  onClick={() => navigate(`/guides?search=${destination.name}`)}
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                >
                  {t('destinations.viewAllGuides', 'View all guides')} →
                </Button>
              </Paper>
            )}

            {/* Nearby Destinations */}
            {destination.nearby?.length > 0 && (
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NearbyIcon sx={{ color: THEME_COLORS.primary.main }} />
                  {t('destinations.nearbyTitle', 'Nearby Destinations')}
                </Typography>
                <Grid container spacing={1}>
                  {destination.nearby.slice(0, 4).map(nearby => (
                    <Grid item xs={6} key={nearby.id}>
                      <Card
                        onClick={() => navigate(`/destinations/${nearby.slug}`)}
                        elevation={0}
                        sx={{
                          cursor: 'pointer', borderRadius: 2, overflow: 'hidden',
                          border: '1px solid', borderColor: 'divider',
                          transition: 'all 0.2s ease',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
                        }}
                      >
                        <Box sx={{ height: 80, position: 'relative' }}>
                          {nearby.image_url ? (
                            <CardMedia component="img" image={nearby.image_url} alt={nearby.name} sx={{ height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Box sx={{ height: '100%', background: `linear-gradient(135deg, ${THEME_COLORS.primary.main}30, ${THEME_COLORS.secondary.main}30)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <LocationIcon sx={{ color: 'rgba(0,0,0,0.15)', fontSize: 32 }} />
                            </Box>
                          )}
                        </Box>
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2, display: 'block' }}>
                            {nearby.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                            {nearby.country}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Structured Data for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "TouristDestination",
        "name": destination.name,
        "description": destination.description,
        "image": destination.image_url,
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": destination.latitude,
          "longitude": destination.longitude
        },
        "containedInPlace": {
          "@type": "Country",
          "name": destination.country
        },
        "touristType": tags,
      })}} />
    </>
  );
};

export default DestinationDetail;
